import React, { useEffect } from 'react';
import moment from 'moment';
import { getCaseType, getNetWorth } from 'Utils';
import { caseTypeCommonFields } from 'Components/Client/Cases/CaseFormFields';
import useIsMobile from 'Hooks/useIsMobile';
import { C, Svg } from './CaseListUI';

// ---------------------------------------------------------------------------
// Viewing a case by stage (Case List handoff §7): the 5-step tracker, answer
// grids, where a case lives in the menu, and the "remember my place" helpers
// used when going from a list to the full case page and back.
// ---------------------------------------------------------------------------

// Case types per main-menu section (names come from the client - keep them).
export const CASE_TYPES = {
  '1': [
    { key: 0, label: 'SOS cases', icon: 'sos', tip: 'Cases that require immediate assistance' },
    { key: 1, label: 'Cases just posted', icon: 'justPosted', tip: 'Cases posted within the past 30 days' },
    { key: 2, label: 'Available cases', icon: 'available', tip: 'Cases posted more than 30 days ago' },
    { key: 3, label: 'Requested cases', icon: 'requested', tip: 'Cases you have requested to view' },
  ],
  '2': [
    { key: 0, label: 'Approved cases', icon: 'approved', tip: 'Cases approved by the client for you to view' },
    { key: 1, label: 'My cases', icon: 'myCases', tip: 'Cases you have cleared conflict for' },
    { key: 2, label: 'Not interested', icon: 'notInterested', tip: 'Cases you marked as not interested' },
  ],
  '3': [
    { key: 0, label: 'Pending closing', icon: 'pendingClosing', tip: 'Cases you have requested to close' },
    { key: 1, label: 'Closed cases', icon: 'closed', tip: 'Cases you have closed' },
  ],
};

export const SECTION_LINK = {
  '1': '/attorney/casesFeed/availableCases',
  '2': '/attorney/casesFeed/activeCases',
  '3': '/attorney/casesFeed/closedCases',
};

// Where a case sits in the menu, from the attorney's UserCase row.
export function placeOf(userCase) {
  const s = userCase?.caseStatus;
  if (s === 'approved') return { section: '2', typeKey: 0 };
  if (s === 'noConflict') return { section: '2', typeKey: userCase?.caseInterest === 1 ? 2 : 1 };
  if (s === 'reqToClose') return { section: '3', typeKey: 0 };
  if (s === 'closed') return { section: '3', typeKey: 1 };
  if (s === 'requested') return { section: '1', typeKey: 3 };
  return { section: '1', typeKey: 2 };
}
export const typeLabel = (section, key) => (CASE_TYPES[section] || []).find((t) => t.key === key)?.label || '';

// ---------- remember the case type per section + the list scroll ----------
const typeStoreKey = (section) => 'qcr.caseType.' + section;
const scrollStoreKey = (section) => 'qcr.listScroll.' + section;
export function readCaseType(section, fallback) {
  try {
    const v = window.sessionStorage.getItem(typeStoreKey(section));
    return v === null || v === '' || isNaN(Number(v)) ? fallback : Number(v);
  } catch (e) { return fallback; }
}
export function writeCaseType(section, key) {
  try { window.sessionStorage.setItem(typeStoreKey(section), String(key)); } catch (e) { /* ignore */ }
}
export function rememberListScroll(section) {
  try { window.sessionStorage.setItem(scrollStoreKey(section), String(window.scrollY || 0)); } catch (e) { /* ignore */ }
}
// Call from a list once its cards are on screen: puts the page back where the
// user was before opening a case (one time only).
export function useRestoreListScroll(section, ready) {
  useEffect(() => {
    if (!ready) return;
    let y = null;
    try { y = window.sessionStorage.getItem(scrollStoreKey(section)); window.sessionStorage.removeItem(scrollStoreKey(section)); } catch (e) { /* ignore */ }
    if (y === null) return;
    const top = Number(y) || 0;
    window.requestAnimationFrame(() => window.scrollTo(0, top));
  }, [section, ready]);
}

// Open the full case page from a list card. The list row goes along as
// navigation state so the page can show dates the details call doesn't send.
export function openCasePage(navigate, row) {
  const caseId = row?.caseDetails?._id || row?.caseId;
  if (!caseId) return;
  const place = placeOf(row);
  writeCaseType(place.section, place.typeKey);
  rememberListScroll(place.section);
  navigate('/attorney/caseDetails/' + caseId, {
    state: {
      fromList: place,
      snapshot: { updated_at: row?.updated_at, created_at: row?.created_at, posted_at: row?.caseDetails?.created_at },
    },
  });
}

// ---------- 5-step tracker ----------
export const STEPS = ['Requested', 'Approved', 'Conflict cleared', 'Requested to close', 'Case closed'];
// index of the step in progress (5 = everything done)
export const stepIndex = (status) => ({ requested: 1, approved: 2, noConflict: 3, reqToClose: 4, closed: 5 }[status] ?? 0);

export function StageTracker({ status }) {
  const isMobile = useIsMobile();
  const cur = stepIndex(status);
  return (
    <div className="flex items-start">
      {STEPS.map((label, i) => {
        const done = i < cur;
        const current = i === cur;
        return (
          <div key={label} className="flex-1 min-w-0 flex flex-col items-center gap-2">
            <div className="flex items-center w-full">
              <span className={`flex-1 h-0.5 ${i <= cur ? 'bg-brass' : 'bg-navy/[.15]'} ${i === 0 ? 'invisible' : 'visible'}`} />
              {done ? (
                <span className="flex-none w-6 h-6 rounded-full bg-brass flex items-center justify-center">
                  <Svg d="M5 12l5 5 9-10" size={12} width={3.2} stroke={C.ivory} />
                </span>
              ) : current ? (
                <span className="flex-none w-6 h-6 box-border rounded-full border-2 border-brass bg-white flex items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-brass" />
                </span>
              ) : (
                <span className="flex-none w-6 h-6 box-border rounded-full border-2 border-navy/[.2] bg-white" />
              )}
              <span className={`flex-1 h-0.5 ${i < cur ? 'bg-brass' : 'bg-navy/[.15]'} ${i === STEPS.length - 1 ? 'invisible' : 'visible'}`} />
            </div>
            <span className={`font-plexsans ${current ? 'font-semibold' : 'font-medium'} ${isMobile ? 'text-[11px]' : 'text-[12.5px]'} leading-[1.25] ${done || current ? 'text-navy' : 'text-navy/[.5]'} text-center px-0.5 [overflow-wrap:anywhere]`}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------- question / answer rows ----------
// full literal class names so Tailwind picks them up; any other column count falls back to an inline template
const GRID_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };
export function AnswerGrid({ rows, cols = 1 }) {
  const isMobile = useIsMobile();
  const n = isMobile ? 1 : cols;
  return (
    <div
      className={`grid ${GRID_COLS[n] || ''} gap-y-0 gap-x-8`}
      style={GRID_COLS[n] ? undefined : { gridTemplateColumns: `repeat(${n},minmax(0,1fr))` }}
    >
      {rows.map((r) => (
        <div key={r.q} className="flex justify-between gap-4 py-[11px] border-b border-navy/[.08]">
          <span className="font-plexsans font-normal text-[13.5px] leading-[1.4] text-navy/[.6] min-w-0">{r.q}</span>
          <span className="font-plexsans font-semibold text-[13.5px] leading-[1.4] text-navy text-right flex-none max-w-[45%] [overflow-wrap:anywhere]">{r.a}</span>
        </div>
      ))}
    </div>
  );
}

export function SectionLabel({ children }) {
  return <span className="font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-navy/[.55]">{children}</span>;
}

// ---------- client's answers ----------
const yn = (v) => (v === 'yes' ? 'Yes' : v === 'no' ? 'No' : v === true ? 'Yes' : v === false ? 'No' : v ? String(v) : '');
const cap = (s) => (s ? String(s).replace(/(^|\s)\S/g, (c) => c.toUpperCase()) : '');
const PREMIUM_ANSWERS = ['isClassAction', 'isEntity', 'defendant', 'adversePartyNetWorth', 'adversePartyPublicCompany'];

// Every answer we have, in the order the client gave them. Premium-only
// answers show "Premium" for free members (same rule as the list cards).
export function clientAnswers(d, premium) {
  if (!d) return [];
  const rows = [];
  const add = (q, a, name) => {
    if (a === undefined || a === null || a === '') return;
    rows.push({ q, a: !premium && PREMIUM_ANSWERS.includes(name) ? 'Premium' : a });
  };
  add('Case category', getCaseType(d.caseType));
  add('When did this happen?', d.dateInfo ? moment(d.dateInfo).format('ddd MMM DD YYYY') : '');
  add('What city did this incident happen in?', cap(d.caseCity));
  add('What state did this incident happen in?', cap(d.caseState));
  add('What zip code did this incident happen in?', d.caseZipcode);
  add('Plaintiff or seeking on behalf of someone?', d.isPostedBySelf ? (d.isPostedBySelf === 'yes' ? 'I am the potential plaintiff' : 'Family member / friend / advisor') : '');
  add('Is this a class action lawsuit?', d.isClassAction ? yn(d.isClassAction) : '', 'isClassAction');
  add('Against an entity or an individual?', d.isEntity ? (d.isEntity === 'yes' ? 'Entity' : 'Individual') : '', 'isEntity');
  add('Other plaintiffs against the same party?', d.defendant ? (d.defendant === 'yes' ? 'Other similar people, too' : 'Just me') : '', 'defendant');
  add("Adverse party's net worth?", d.adversePartyNetWorth ? getNetWorth(d.adversePartyNetWorth) : '', 'adversePartyNetWorth');
  add('Is the adverse party a public company?', d.adversePartyPublicCompany ? (d.adversePartyPublicCompany === 'yes' ? 'Yes' : d.adversePartyPublicCompany === 'no' ? 'No' : 'Unknown') : '', 'adversePartyPublicCompany');
  add('Currently represented by an attorney?', d.attAvailable ? yn(d.attAvailable) : '');
  // case-type questions (only sent once the conflict check is cleared)
  let extra = null;
  try { extra = typeof d.additionalFields === 'string' ? JSON.parse(d.additionalFields) : d.additionalFields; } catch (e) { extra = null; }
  if (extra && typeof extra === 'object') {
    (caseTypeCommonFields || []).forEach((group) => (group.fields || []).forEach((f) => {
      const v = extra[f.name];
      if (v === undefined || v === null || v === '') return;
      add(f.label, Array.isArray(v) ? v.join(', ') : String(v));
    }));
  }
  if (d.additionalNotes) add('Anything else the client wanted to share', d.additionalNotes);
  return rows;
}

// The few answers a requested card doesn't already show.
export function requestedAnswers(d) {
  if (!d) return [];
  const rows = [];
  if (d.dateInfo) rows.push({ q: 'When did this happen?', a: moment(d.dateInfo).format('ddd MMM DD YYYY') });
  if (d.isPostedBySelf) rows.push({ q: 'Plaintiff or seeking on behalf of someone?', a: d.isPostedBySelf === 'yes' ? 'I am the potential plaintiff' : 'Family member / friend / advisor' });
  if (d.attAvailable) rows.push({ q: 'Currently represented by an attorney?', a: yn(d.attAvailable) });
  return rows;
}

// client name without the literal "null"/"undefined" strings some cases were saved with
const cleanPart = (v) => (v && !['null', 'undefined'].includes(String(v).trim().toLowerCase()) ? String(v).trim() : '');
export const clientNameOf = (d) => [cleanPart(d?.firstName), cleanPart(d?.lastName)].filter(Boolean).join(' ');

export const fmtDate = (v) => (v ? moment(v).format('MM/DD/YY') : '');
